import { useCallback, useMemo, useRef, useState } from 'react'
import type { Updater, ColumnFiltersState, PaginationState, VisibilityState } from '@tanstack/react-table'
import type { ColumnDefinition } from '../types'
import type { TablePreferencesV1 } from '../types'
import {
  getDefaultTablePreferences,
  loadTablePreferences,
  saveTablePreferences,
} from '../tablePreferences'
import { resolveFilterableColumns } from '../filterColumns'

export interface UseTablePreferencesOptions<TRow> {
  userId: string
  tableId: string
  columns: ColumnDefinition<TRow>[]
  disablePersistence?: boolean
  defaults?: Partial<Omit<TablePreferencesV1, 'version'>>
}

export function useTablePreferences<TRow>({
  userId,
  tableId,
  columns,
  disablePersistence = false,
  defaults,
}: UseTablePreferencesOptions<TRow>) {
  const persistEnabled = Boolean(
    userId &&
      userId !== 'anonymous' &&
      userId !== 'null' &&
      userId !== 'undefined' &&
      tableId &&
      !disablePersistence,
  )

  const [prefs, setPrefs] = useState<TablePreferencesV1>(() =>
    persistEnabled
      ? loadTablePreferences(userId, tableId, columns, defaults)
      : getDefaultTablePreferences(columns, defaults),
  )

  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const persist = useCallback(
    (next: TablePreferencesV1) => {
      if (!persistEnabled) return
      if (persistTimer.current) clearTimeout(persistTimer.current)
      persistTimer.current = setTimeout(() => {
        saveTablePreferences(userId, tableId, next)
        persistTimer.current = null
      }, 300)
    },
    [persistEnabled, userId, tableId],
  )

  const updatePrefs = useCallback(
    (updater: (prev: TablePreferencesV1) => TablePreferencesV1) => {
      setPrefs((prev) => {
        const next = updater(prev)
        persist(next)
        return next
      })
    },
    [persist],
  )

  const orderedColumns = useMemo(() => {
    const map = new Map(columns.map((c) => [c.id, c]))
    return prefs.columnOrder
      .map((id) => map.get(id))
      .filter((c): c is ColumnDefinition<TRow> => Boolean(c))
  }, [columns, prefs.columnOrder])

  const filterableColumns = useMemo(
    () =>
      resolveFilterableColumns(
        orderedColumns,
        prefs.visibleFilterIds,
        prefs.columnFilters,
      ),
    [orderedColumns, prefs.visibleFilterIds, prefs.columnFilters],
  )

  const onPaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      updatePrefs((prev) => ({
        ...prev,
        pagination:
          typeof updater === 'function' ? updater(prev.pagination) : updater,
      }))
    },
    [updatePrefs],
  )

  const onColumnFiltersChange = useCallback(
    (updater: Updater<ColumnFiltersState>) => {
      updatePrefs((prev) => ({
        ...prev,
        columnFilters:
          typeof updater === 'function' ? updater(prev.columnFilters) : updater,
        pagination: { ...prev.pagination, pageIndex: 0 },
      }))
    },
    [updatePrefs],
  )

  const onSortChange = useCallback(
    (sort: { sort_by: string; order: 'asc' | 'desc' }) => {
      updatePrefs((prev) => ({
        ...prev,
        sort,
        pagination: { ...prev.pagination, pageIndex: 0 },
      }))
    },
    [updatePrefs],
  )

  const onColumnVisibilityChange = useCallback(
    (updater: VisibilityState | Updater<VisibilityState>) => {
      updatePrefs((prev) => {
        const nextVis =
          typeof updater === 'function'
            ? updater(prev.columnVisibility)
            : updater
        return { ...prev, columnVisibility: nextVis }
      })
    },
    [updatePrefs],
  )

  const onColumnOrderChange = useCallback(
    (updater: string[] | Updater<string[]>) => {
      updatePrefs((prev) => ({
        ...prev,
        columnOrder:
          typeof updater === 'function' ? updater(prev.columnOrder) : updater,
      }))
    },
    [updatePrefs],
  )

  const onVisibleFilterIdsChange = useCallback(
    (ids: string[]) => {
      updatePrefs((prev) => ({ ...prev, visibleFilterIds: ids }))
    },
    [updatePrefs],
  )

  const onColumnSizingChange = useCallback(
    (updater: Updater<Record<string, number>>) => {
      updatePrefs((prev) => ({
        ...prev,
        columnSizing:
          typeof updater === 'function'
            ? updater(prev.columnSizing ?? {})
            : updater,
      }))
    },
    [updatePrefs],
  )

  const setSearch = useCallback(
    (search: string) => {
      updatePrefs((prev) => ({
        ...prev,
        search,
        pagination: { ...prev.pagination, pageIndex: 0 },
      }))
    },
    [updatePrefs],
  )

  const resetPageIndex = useCallback(() => {
    updatePrefs((prev) => ({
      ...prev,
      pagination: { ...prev.pagination, pageIndex: 0 },
    }))
  }, [updatePrefs])

  return {
    prefs,
    orderedColumns,
    filterableColumns,
    allFilterColumns: useMemo(
      () => orderedColumns.filter((c) => c.filterType),
      [orderedColumns],
    ),
    onPaginationChange,
    onColumnFiltersChange,
    onSortChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onVisibleFilterIdsChange,
    onColumnSizingChange,
    setSearch,
    resetPageIndex,
  }
}
